import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Typography, Row, Col, Statistic, Spin, message, Table, Tag,
  Progress, Space, Button, Alert, Input, Select, Empty
} from 'antd';
import {
  DollarOutlined, SyncOutlined, ShopOutlined, SwapOutlined,
  SearchOutlined, FilterOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { analysisService } from '../services/api';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Alternatives = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const navigate = useNavigate();

  const fetchAlternatives = async () => {
    try {
      setLoading(true);
      // First run the analysis to generate/update recommendations
      await analysisService.analyzeVendors('alternatives', null, 0.7);
      // Then fetch the alternatives
      const result = await analysisService.getRecommendations({ recommendation_type: 'alternative' });
      setRecommendations(result);
    } catch (error) {
      console.error('Error fetching alternatives:', error);
      message.error('Failed to load alternative recommendations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Just fetch existing recommendations on load (don't re-run analysis)
    const fetchExisting = async () => {
      try {
        setLoading(true);
        const result = await analysisService.getRecommendations({ recommendation_type: 'alternative' });
        setRecommendations(result);
      } catch (error) {
        console.error('Error fetching alternatives:', error);
        message.error('Failed to load alternative recommendations');
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAlternatives();
    setRefreshing(false);
    message.success('Alternatives analysis refreshed');
  };

  // Get unique vendors from recommendations for filtering
  const uniqueVendors = useMemo(() => {
    const vendors = new Map();
    recommendations.forEach(rec => {
      if (rec.product?.vendor) {
        vendors.set(rec.product.vendor.id, rec.product.vendor.name);
      }
    });
    return Array.from(vendors, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [recommendations]);

  // Filter recommendations
  const filteredRecommendations = useMemo(() => {
    let result = recommendations;

    if (selectedVendor) {
      result = result.filter(rec => rec.product?.vendor?.id === selectedVendor);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(rec =>
        rec.product?.name?.toLowerCase().includes(query) ||
        rec.product?.vendor?.name?.toLowerCase().includes(query) ||
        rec.similar_product?.name?.toLowerCase().includes(query) ||
        rec.similar_product?.vendor?.name?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [recommendations, selectedVendor, searchQuery]);

  // Group recommendations by current vendor for summary
  const summaryStats = useMemo(() => {
    const altVendors = new Set();
    const currentVendors = new Set();
    let totalPotentialSavings = 0;

    recommendations.forEach(rec => {
      if (rec.similar_product?.vendor) {
        altVendors.add(rec.similar_product.vendor.id);
      }
      if (rec.product?.vendor) {
        currentVendors.add(rec.product.vendor.id);
      }
      totalPotentialSavings += rec.potential_savings || 0;
    });

    return {
      totalAlternatives: recommendations.length,
      alternativeVendors: altVendors.size,
      currentVendorsWithAlts: currentVendors.size,
      totalPotentialSavings
    };
  }, [recommendations]);

  const columns = [
    {
      title: 'Your Product',
      key: 'current',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.product?.name || 'Unknown'}</Text>
          <Tag color="blue">{record.product?.vendor?.name || 'Unknown Vendor'}</Tag>
          {record.product?.category && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.product.category}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => <SwapOutlined style={{ color: '#999', fontSize: 18 }} />,
    },
    {
      title: 'Alternative Product',
      key: 'alternative',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#52c41a' }}>{record.similar_product?.name || 'Unknown'}</Text>
          <Tag color="green">{record.similar_product?.vendor?.name || 'Unknown Vendor'}</Tag>
          {record.similar_product?.category && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.similar_product.category}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Similarity',
      dataIndex: 'similarity_score',
      key: 'similarity',
      width: 150,
      sorter: (a, b) => (a.similarity_score || 0) - (b.similarity_score || 0),
      render: (score) => (
        <Progress
          percent={Math.round((score || 0) * 100)}
          size="small"
          status={score > 0.85 ? "success" : "normal"}
          format={percent => `${percent}%`}
        />
      ),
    },
    {
      title: 'Potential Savings',
      dataIndex: 'potential_savings',
      key: 'savings',
      width: 140,
      sorter: (a, b) => (a.potential_savings || 0) - (b.potential_savings || 0),
      render: (savings) => (
        savings > 0 ? (
          <Text strong style={{ color: '#52c41a' }}>
            ${(savings || 0).toLocaleString()}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.is_implemented ? (
            <Tag color="success">Implemented</Tag>
          ) : record.is_reviewed ? (
            <Tag color="processing">Reviewed</Tag>
          ) : (
            <Tag color="default">New</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/product-database?vendor=${record.similar_product?.vendor?.id}`)}
        >
          View Vendor
        </Button>
      ),
    },
  ];

  if (loading && recommendations.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading alternative recommendations...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2}>
            <ThunderboltOutlined style={{ marginRight: 12 }} />
            Alternative Vendors
          </Title>
          <Text type="secondary">
            Discover similar products from vendors you don't currently use
          </Text>
        </div>
        <Button
          type="primary"
          icon={<SyncOutlined spin={refreshing} />}
          onClick={handleRefresh}
          loading={refreshing}
        >
          Refresh Analysis
        </Button>
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Alternatives Found"
              value={summaryStats.totalAlternatives}
              prefix={<SwapOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Alternative Vendors"
              value={summaryStats.alternativeVendors}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Your Vendors with Alternatives"
              value={summaryStats.currentVendorsWithAlts}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Potential Savings"
              value={summaryStats.totalPotentialSavings}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#cf1322' }}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Info Alert */}
      <Alert
        message="Alternative Vendor Recommendations"
        description="These are products from vendors you don't currently use that are similar to products from your current vendors. Consider these alternatives when negotiating contracts or looking to reduce costs."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong><FilterOutlined /> Filters:</Text>
          </Col>
          <Col flex="auto">
            <Space size="middle" wrap>
              <Search
                placeholder="Search products or vendors..."
                allowClear
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Select
                placeholder="Filter by your vendor"
                style={{ width: 200 }}
                allowClear
                value={selectedVendor}
                onChange={setSelectedVendor}
              >
                {uniqueVendors.map(vendor => (
                  <Option key={vendor.id} value={vendor.id}>{vendor.name}</Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Results Table */}
      <Card title={`Alternatives (${filteredRecommendations.length})`}>
        <Spin spinning={loading}>
          {filteredRecommendations.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredRecommendations}
              rowKey="id"
              pagination={{ pageSize: 15, showSizeChanger: true }}
            />
          ) : (
            <Empty
              description={
                <span>
                  No alternative recommendations found.
                  {recommendations.length === 0 && (
                    <>
                      <br />
                      <Button type="primary" onClick={handleRefresh} style={{ marginTop: 16 }}>
                        Run Analysis
                      </Button>
                    </>
                  )}
                </span>
              }
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default Alternatives;
